import { useEffect, useState, type ReactNode } from "react";
import { NodeSelection, type Command } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import {
  deleteBlockAt,
  duplicateBlockAt,
  moveBlock,
  selectBlockAt,
  setParagraph,
  toggleBlockquote,
  toggleHeading,
} from "../commands/blocks";
import { BULLET_LIST, TODO_LIST, orderedList, toggleList } from "../commands/lists";
import type { EditorContext } from "../context";
import { MenuPopover, Separator, ToolbarButton, placementAtPos } from "./MenuPopover";
import { SLASH_ITEMS } from "./slash-items";

export interface BlockHandleProps {
  view: EditorView;
  context: EditorContext;
}

interface Target {
  pos: number;
  top: number;
  left: number;
}

/**
 * The gutter drag handle of `editor.md` §6. Hovering a top-level block reveals
 * it; dragging moves the block (ProseMirror's own drag of a node selection);
 * clicking opens a menu with the insert items plus move, duplicate and delete
 * for the block under the handle.
 */
export function BlockHandle({ view, context }: BlockHandleProps): ReactNode {
  const [target, setTarget] = useState<Target | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (context.readOnly) return;
    const dom = view.dom;
    const onMove = (event: MouseEvent): void => {
      const found = view.posAtCoords({ left: event.clientX, top: event.clientY });
      if (!found) return;
      const $pos = view.state.doc.resolve(found.inside >= 0 ? found.inside : found.pos);
      const depth = Math.min(1, $pos.depth);
      const pos = depth === 0 ? 0 : $pos.before(1);
      const node = view.nodeDOM(pos);
      const rect = node instanceof HTMLElement ? node.getBoundingClientRect() : null;
      setTarget({ pos, top: rect?.top ?? 0, left: (rect?.left ?? 0) - 28 });
    };
    const onLeave = (): void => {
      if (!open) setTarget(null);
    };
    dom.addEventListener("mousemove", onMove);
    dom.addEventListener("mouseleave", onLeave);
    return () => {
      dom.removeEventListener("mousemove", onMove);
      dom.removeEventListener("mouseleave", onLeave);
    };
  }, [context.readOnly, open, view]);

  if (context.readOnly || !target) return null;

  const run = (command: Command): void => {
    command(view.state, view.dispatch, view);
    view.focus();
    setOpen(false);
  };

  const selectBlock = (): void => {
    selectBlockAt(target.pos)(view.state, view.dispatch, view);
  };

  return (
    <>
      <button
        type="button"
        className="wiki-block-handle"
        aria-label="Block actions"
        data-testid="block-handle"
        draggable
        style={{ top: `${String(target.top)}px`, left: `${String(target.left)}px` }}
        onDragStart={() => {
          selectBlock();
        }}
        onClick={() => {
          selectBlock();
          setOpen(!open);
        }}
      />
      {open && (
        <MenuPopover
          placement={placementAtPos(view, target.pos)}
          label="Block actions"
          testId="block-handle-menu"
          className="wiki-menu--list"
          onDismiss={() => {
            setOpen(false);
          }}
        >
          <div className="wiki-menu__row">
            <ToolbarButton
              label="Move up"
              onSelect={() => {
                run(moveBlock(-1));
              }}
            />
            <ToolbarButton
              label="Move down"
              onSelect={() => {
                run(moveBlock(1));
              }}
            />
            <ToolbarButton
              label="Duplicate"
              onSelect={() => {
                run(duplicateBlockAt(target.pos));
              }}
            />
            <ToolbarButton
              label="Delete"
              onSelect={() => {
                run(deleteBlockAt(target.pos));
              }}
            />
            <Separator />
            <ToolbarButton
              label="Text"
              onSelect={() => {
                run(setParagraph);
              }}
            />
            <ToolbarButton
              label="Big heading"
              onSelect={() => {
                run(toggleHeading(1));
              }}
            />
            <ToolbarButton
              label="Quote"
              onSelect={() => {
                run(toggleBlockquote);
              }}
            />
            <ToolbarButton
              label="Bulleted list"
              onSelect={() => {
                run(toggleList(BULLET_LIST));
              }}
            />
            <ToolbarButton
              label="Ordered list"
              onSelect={() => {
                run(toggleList(orderedList("number")));
              }}
            />
            <ToolbarButton
              label="Todo list"
              onSelect={() => {
                run(toggleList(TODO_LIST));
              }}
            />
          </div>
          <ul className="wiki-menu__list">
            {SLASH_ITEMS.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="wiki-menu__item"
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    // Insert at the block, not at the stale caret.
                    if (view.state.doc.nodeAt(target.pos)) {
                      view.dispatch(
                        view.state.tr.setSelection(
                          NodeSelection.create(view.state.doc, target.pos),
                        ),
                      );
                    }
                    item.run(view, context);
                    setOpen(false);
                  }}
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
        </MenuPopover>
      )}
    </>
  );
}
